import {
  CustomFieldDefinition,
  CustomFields,
  CustomFieldsMapper,
  HexColor,
  LocationMapper,
} from '@nx-smartmonkey/shared/domain';
import { Driver } from '../../domain/driver/Driver';
import { Vehicle } from '../../domain/vehicle/Vehicle';
import { VehicleDTO } from '../../domain/vehicle/VehicleDTO';
import { VehicleRepositoryDTO } from '../../domain/vehicle/VehicleRepositoryDTO';

export class VehicleMapper {
  static toDomain(props: VehicleDTO, customFieldDefinitions: CustomFieldDefinition[]): Vehicle {
    const {
      id,
      organization_id,
      project_id,
      avatar_url,
      external_id,
      label,
      phone,
      email,
      plate,
      vehicle_model,
      color,
      brand,
      comments,
      default_time_window,
      default_max_weight,
      default_max_volume,
      default_max_services,
      default_max_distance,
      default_min_distance,
      default_max_time,
      default_min_time,
      default_start_location,
      default_end_location,
      default_provides,
      default_geo_fences,
      custom_fields,
      price_per_distance,
      price_per_minute,
      enabled,
      deleted,
      created_by,
      created_at,
      updated_at,
      deleted_at,
    } = props;

    const colorDomain = color ? HexColor.create(color) : undefined;
    const defaultStartLocation = default_start_location ? LocationMapper.toDomain(default_start_location) : undefined;
    const defaultEndLocation = default_end_location ? LocationMapper.toDomain(default_end_location) : undefined;

    return Vehicle.create(
      {
        organization_id,
        project_id,
        avatar_url,
        external_id,
        label,
        phone,
        email,
        plate,
        vehicle_model,
        color: colorDomain,
        brand,
        comments,
        default_time_window,
        default_max_weight,
        default_max_volume,
        default_max_services,
        default_max_distance,
        default_min_distance,
        default_max_time,
        default_min_time,
        default_start_location: defaultStartLocation,
        default_end_location: defaultEndLocation,
        default_provides,
        default_geo_fences,
        custom_fields: custom_fields ? CustomFields.create(custom_fields, customFieldDefinitions) : undefined,
        price_per_distance,
        price_per_minute,
        enabled,
        deleted,
        created_by,
        created_at,
        updated_at,
        deleted_at,
      },
      id
    );
  }

  static toDTO(vehicle: Vehicle): VehicleDTO {
    return {
      id: vehicle.id,
      organization_id: vehicle.organization_id,
      project_id: vehicle.project_id,
      avatar_url: vehicle.avatar_url,
      external_id: vehicle.external_id,
      label: vehicle.label,
      phone: vehicle.phone,
      email: vehicle.email,
      plate: vehicle.plate,
      vehicle_model: vehicle.vehicle_model,
      color: vehicle.color ? vehicle.color.value : undefined,
      brand: vehicle.brand,
      comments: vehicle.comments,
      default_time_window: vehicle.default_time_window,
      default_max_weight: vehicle.default_max_weight,
      default_max_volume: vehicle.default_max_volume,
      default_max_services: vehicle.default_max_services,
      default_max_distance: vehicle.default_max_distance,
      default_min_distance: vehicle.default_min_distance,
      default_max_time: vehicle.default_max_time,
      default_min_time: vehicle.default_min_time,
      default_start_location: vehicle.default_start_location
        ? LocationMapper.toDTO(vehicle.default_start_location)
        : undefined,
      default_end_location: vehicle.default_end_location
        ? LocationMapper.toDTO(vehicle.default_end_location)
        : undefined,
      default_provides: vehicle.default_provides,
      default_geo_fences: vehicle.default_geo_fences,
      custom_fields: vehicle.custom_fields,
      price_per_distance: vehicle.price_per_distance,
      price_per_minute: vehicle.price_per_minute,
      enabled: vehicle.enabled,
      deleted: vehicle.deleted,
      created_by: vehicle.created_by,
      created_at: vehicle.created_at,
      updated_at: vehicle.updated_at,
      deleted_at: vehicle.deleted_at,
    };
  }

  static toDriver(vehicle: Vehicle): Driver {
    const {
      id,
      project_id,
      default_end_location,
      default_start_location,
      default_max_distance,
      default_min_distance,
      default_max_time,
      default_min_time,
      default_max_volume,
      default_max_weight,
      default_provides,
      default_time_window,
      default_max_services,
      default_geo_fences,
      plate,
      vehicle_model,
      color,
      brand,
      phone,
      label,
      email,
      custom_fields,
      price_per_distance,
      price_per_minute,
      external_id,
    } = vehicle;

    return Driver.create({
      vehicle_id: id,
      status: `not_started`,
      project_id,
      start_location: default_start_location,
      end_location: default_end_location,

      max_distance: default_max_distance,
      min_distance: default_min_distance,
      max_time: default_max_time,
      min_time: default_min_time,
      max_volume: default_max_volume,
      max_weight: default_max_weight,
      max_services: default_max_services,

      provides: default_provides,
      geo_fences: default_geo_fences,
      time_window: default_time_window,
      external_id,
      plate,
      vehicle_model,
      color,
      brand,
      phone,
      label,
      email,
      custom_fields,
      price_per_distance,
      price_per_minute,
      stops: [],
    });
  }

  static toRepository(vehicle: Vehicle): VehicleRepositoryDTO {
    const {
      external_id,
      label,
      phone,
      email,
      plate,
      vehicle_model,
      color,
      brand,
      comments,
      default_time_window,
      default_max_weight,
      default_max_volume,
      default_max_services,
      default_max_distance,
      default_min_distance,
      default_max_time,
      default_min_time,
      default_start_location,
      default_end_location,
      default_provides,
      default_geo_fences,
      custom_fields,
      price_per_distance,
      price_per_minute,
    } = vehicle;

    return {
      external_id,
      label,
      phone,
      email,
      plate,
      vehicle_model,
      color: color?.value,
      brand,
      comments,
      default_time_window,
      default_max_weight,
      default_max_volume,
      default_max_services,
      default_max_distance,
      default_min_distance,
      default_max_time,
      default_min_time,
      ...(default_start_location !== undefined
        ? {
            default_start_location:
              default_start_location !== null ? LocationMapper.toDTO(default_start_location) : null,
          }
        : {}),
      ...(default_end_location !== undefined
        ? {
            default_end_location: default_end_location !== null ? LocationMapper.toDTO(default_end_location) : null,
          }
        : {}),
      default_provides,
      default_geo_fences,
      custom_fields: custom_fields ? CustomFieldsMapper.toRepository(custom_fields) : undefined,
      price_per_distance,
      price_per_minute,
    };
  }
}
